/**
 * @file Defines the full set of investment export columns and each column's value getter.
 */
import { getAddress } from "@components/Address";
import { getCurrencyValue } from "@components/Amount";
import { formatPhoneNumber } from "@components/Text";
import { getUsDateFromIsoDate } from "@components/Date";
import {
  isTrustInvestment,
  isSdiraInvestment,
  isJointInvestment,
  isEntityInvestment,
  isIndividualInvestment,
  computeInvestmentAmount,
} from "@components/Investment";
import {
  PROJECT_TYPES,
  ENTITY_TYPE_LLC,
  PROFILE_TYPES_LABELS,
  ENTITY_TYPE_TITLES_MAP,
  INVESTMENT_PROFILE_FIELD_MAP,
  ENTITY_TYPE_EXEMPT_ORGANIZATION,
  ENTITY_TAX_FILING_STATUS_TITLES_MAP,
  JOINT_MEMBERSHIP_INTERESTS_TITLES_MAP,
  ENTITY_MEMBERSHIP_INTERESTS_TITLES_MAP,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA,
  BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL,
} from "@components/Domain";
import {
  CUSTODIAN_REVIEWED_SDIRA,
  CHECKBOOK_CONTROLLED_SDIRA,
} from "@api/services/investments/shapes/SdiraTypeEnum";

import getBeneficiary from "./getBeneficiary";
import getEntityNames from "./getEntityNames";
import getUsResidenceState from "./getUsResidenceState";
import getAccreditation, { ACCREDITATION_VALUE_NO, ACCREDITATION_VALUE_YES } from "./getAccreditation";
import type {
  Column,
  Account,
  Project,
  Property,
  Investment,
  ProfileField,
  BankAccountsMap,
  ResidencyProfile,
} from "../types";

const SDIRA_TYPE_LABELS_MAP: Record<string, string> = {
  [CUSTODIAN_REVIEWED_SDIRA]: "Custodian-reviewed SDIRA",
  [CHECKBOOK_CONTROLLED_SDIRA]: "Checkbook-controlled SDIRA",
};

const EMPTY_VALUE = "-";

const LABEL_NO = "No";
const LABEL_YES = "Yes";
const LABEL_SIGNED = "SIGNED";
const LABEL_PENDING = "PENDING";

const LABEL_US_CITIZEN = "is US citizen";
const LABEL_NOT_US_CITIZEN = "is not US citizen";

const LABEL_NO_ACCREDITATION = "No Accreditation";
const LABEL_ACCREDITATION_VERIFIED = "Accreditation Verified";
const LABEL_UNVERIFIED_ACCREDITATION = "Unverified Accreditation";

const LABEL_SINGLE_PERSON = "Individually, as a single person";
const LABEL_MARRIED_PERSON = "A married person, as my sole and separate property";

const LABEL_DISREGARDED = "Disregarded for federal income tax purposes";
const LABEL_NOT_DISREGARDED = "Not disregarded for federal income tax purposes";

const MEMBERSHIP_TITLE_PREFIX = "As ";
const NAME_PARTS_SEPARATOR = " ";
const DETAILS_SEPARATOR = ", ";

const WHITESPACE_REGEX = /\s+/g;

type GetAccount = (accountId: string) => Account;
type GetProperty = (propertyId: string) => Property;
type GetSponsorName = (sponsorId?: string) => string;
type ComputeUserFullname = (userId?: string) => string;

type Options = {
  project: Project;
  getAccount: GetAccount;
  properties: Property[];
  getProperty: GetProperty;
  classColumns?: Column[];
  getSponsorName: GetSponsorName;
  bankAccountsMap: BankAccountsMap;
  computeUserFullname: ComputeUserFullname;
};

/**
 * Collapses repeated whitespace in a name into single spaces and trims it.
 */
export const formatName = (name: string): string => {
  const formattedName = name.replace(WHITESPACE_REGEX, NAME_PARTS_SEPARATOR).trim();

  return formattedName;
};

/**
 * Maps a raw accreditation value to its export label.
 */
const getAccreditationValue = (accreditationValue: string): string => {
  const isAccreditation = accreditationValue === ACCREDITATION_VALUE_YES;
  const isNoAccreditation = accreditationValue === ACCREDITATION_VALUE_NO;

  if (isNoAccreditation) {
    return LABEL_NO_ACCREDITATION;
  }

  if (isAccreditation) {
    return LABEL_ACCREDITATION_VERIFIED;
  }

  return LABEL_UNVERIFIED_ACCREDITATION;
};

/**
 * Returns the investment's residency profile that matches its profile type.
 */
const getProfile = (investment: Investment): ResidencyProfile | undefined => {
  const { profileType } = investment;

  const profileField = INVESTMENT_PROFILE_FIELD_MAP[profileType] as ProfileField;
  const profile = investment[profileField];

  return profile;
};

/**
 * Builds the ordered list of export columns for an investment.
 */
const getInvestmentExportColumns = ({
  classColumns = [],
  project,
  getAccount,
  properties,
  getProperty,
  getSponsorName,
  bankAccountsMap,
  computeUserFullname,
}: Options): Column[] => {
  const {
    type: projectType,
    name: projectName,
    closeDate: projectCloseDate,
  } = project;

  const isNonFund = projectType !== PROJECT_TYPES.FUND;

  /**
   * Looks up the investor for an investment's account id.
   */
  const getInvestor = (investorAccountId: string) => {
    const account = getAccount(investorAccountId);
    const { investor } = account;

    return investor;
  };

  return [
    {
      title: "Project Name",
      /**
       * Returns the export value for the "Project Name" column.
       */
      value: () => `${projectName}`.trim(),
    },
    {
      title: "Entity Name(s)",
      /**
       * Returns the export value for the "Entity Name(s)" column.
       */
      value: () => getEntityNames(properties, getProperty, project),
    },
    {
      title: "Beneficiary",
      /**
       * Returns the export value for the "Beneficiary" column.
       */
      value: () => getBeneficiary(bankAccountsMap, project),
    },
    {
      title: "Sponsor",
      /**
       * Returns the export value for the "Sponsor" column.
       */
      value: ({ sponsorId }) => getSponsorName(sponsorId),
    },
    {
      title: "Liaison",
      /**
       * Returns the export value for the "Liaison" column.
       */
      value: ({ brokerUserId }) => computeUserFullname(brokerUserId),
    },
    {
      title: "Investment ID",
      /**
       * Returns the export value for the "Investment ID" column.
       */
      value: ({ id }) => id,
    },
    {
      title: "Investment Profile Name",
      /**
       * Returns the export value for the "Investment Profile Name" column.
       */
      value: ({ name }) => name,
    },
    {
      title: "Investor Legal Full Name",
      /**
       * Returns the export value for the "Investor Legal Full Name" column.
       */
      value: ({ investorAccountId }) => {
        const { givenName, familyName, additionalName } = getInvestor(investorAccountId);

        const fullName = [ givenName, additionalName, familyName ].join(NAME_PARTS_SEPARATOR);

        return formatName(fullName);
      },
    },
    {
      title: "DOB",
      /**
       * Returns the export value for the "DOB" column.
       */
      value: ({ investorAccountId }) => {
        const { dateOfBirth } = getInvestor(investorAccountId);

        const formattedDate = dateOfBirth
          ? getUsDateFromIsoDate(dateOfBirth)
          : "";

        return formattedDate;
      },
    },
    {
      title: "Investor Address",
      /**
       * Returns the export value for the "Investor Address" column.
       */
      value: ({ investorAccountId }) => {
        const { address } = getInvestor(investorAccountId);

        return getAddress(address);
      },
    },
    {
      title: "Contact Phone Number",
      /**
       * Returns the export value for the "Contact Phone Number" column.
       */
      value: ({ investorAccountId }) => {
        const { mobileNumber } = getInvestor(investorAccountId);

        return formatPhoneNumber(mobileNumber);
      },
    },
    {
      title: "Email Address",
      /**
       * Returns the export value for the "Email Address" column.
       */
      value: ({ investorAccountId }) => {
        const { email } = getInvestor(investorAccountId);

        return email;
      },
    },
    {
      title: "Additional Details",
      /**
       * Returns the export value for the "Additional Details" column.
       */
      value: (investment) => {
        const { isFunded, investor } = investment;
        const { hasAccreditation, accreditationExpiresAt } = investor;

        const {
          value: accreditationValue,
        } = getAccreditation(hasAccreditation, accreditationExpiresAt, projectCloseDate, isFunded, isNonFund);

        const accreditation = getAccreditationValue(accreditationValue);

        const profile = getProfile(investment);

        if (!profile) {
          return accreditation;
        }

        const { isUsCitizen } = profile;

        const hasUsCitizenValue = isUsCitizen !== undefined;

        if (!hasUsCitizenValue) {
          return accreditation;
        }

        const citizenship = isUsCitizen
          ? LABEL_US_CITIZEN
          : LABEL_NOT_US_CITIZEN;

        return [ accreditation, citizenship ].join(DETAILS_SEPARATOR);
      },
    },
    {
      title: "Investment Profile Type",
      /**
       * Returns the export value for the "Investment Profile Type" column.
       */
      value: ({ profileType }) => PROFILE_TYPES_LABELS[profileType] || "",
    },
    {
      title: "Title of membership interests",
      /**
       * Returns the export value for the "Title of membership interests" column.
       */
      value: (investment) => {
        const { profileIndividual, profileJoint, profileEntity } = investment;

        const isIndividual = isIndividualInvestment(investment);

        if (isIndividual) {
          if (!profileIndividual) {
            return undefined;
          }

          const { isMarriedAndSeparateProperty } = profileIndividual;

          const title = isMarriedAndSeparateProperty
            ? LABEL_MARRIED_PERSON
            : LABEL_SINGLE_PERSON;

          return title;
        }

        const isJoint = isJointInvestment(investment);

        if (isJoint) {
          if (!profileJoint) {
            return undefined;
          }

          const { membershipInterestsTitle } = profileJoint;

          return JOINT_MEMBERSHIP_INTERESTS_TITLES_MAP[membershipInterestsTitle];
        }

        const isEntity = isEntityInvestment(investment);

        if (isEntity) {
          if (!profileEntity) {
            return undefined;
          }

          const { membershipInterestsTitle } = profileEntity;

          const title = ENTITY_MEMBERSHIP_INTERESTS_TITLES_MAP[membershipInterestsTitle]?.replace(MEMBERSHIP_TITLE_PREFIX, "");

          return title;
        }

        return EMPTY_VALUE;
      },
    },
    {
      title: "SSN/ITIN",
      /**
       * Returns the export value for the "SSN/ITIN" column.
       */
      value: (investment) => {
        const { profileJoint, profileIndividual } = investment;

        const isIndividual = isIndividualInvestment(investment);

        if (isIndividual) {
          if (!profileIndividual) {
            return undefined;
          }

          return profileIndividual.ssnNumber;
        }

        const isJoint = isJointInvestment(investment);

        if (isJoint) {
          if (!profileJoint) {
            return undefined;
          }

          return profileJoint.ssnNumber;
        }

        return EMPTY_VALUE;
      },
    },
    {
      title: "Co-Investor Legal Full Name",
      /**
       * Returns the export value for the "Co-Investor Legal Full Name" column.
       */
      value: (investment) => {
        const isJoint = isJointInvestment(investment);

        if (!isJoint) {
          return EMPTY_VALUE;
        }

        const { profileJoint } = investment;

        if (!profileJoint) {
          return undefined;
        }

        const {
          jointGivenName,
          jointFamilyName,
          jointAdditionalName,
        } = profileJoint;

        return [ jointGivenName, jointAdditionalName, jointFamilyName ].join(NAME_PARTS_SEPARATOR);
      },
    },
    {
      title: "Co-Investor Email",
      /**
       * Returns the export value for the "Co-Investor Email" column.
       */
      value: (investment) => {
        const isJoint = isJointInvestment(investment);

        if (!isJoint) {
          return EMPTY_VALUE;
        }

        const { profileJoint } = investment;

        if (!profileJoint) {
          return undefined;
        }

        const { shouldCoInvestorSign } = profileJoint;

        if (!shouldCoInvestorSign) {
          return EMPTY_VALUE;
        }

        return profileJoint.jointEmail;
      },
    },
    {
      title: "Joint Address",
      /**
       * Returns the export value for the "Joint Address" column.
       */
      value: (investment) => {
        const isJoint = isJointInvestment(investment);

        if (!isJoint) {
          return EMPTY_VALUE;
        }

        const { profileJoint } = investment;

        if (!profileJoint) {
          return undefined;
        }

        return getAddress(profileJoint.jointAddress);
      },
    },
    {
      title: "Entity's Full Legal Name",
      /**
       * Returns the export value for the "Entity's Full Legal Name" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const isTrust = isTrustInvestment(investment);

        if (isTrust) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        return profileEntity.name;
      },
    },
    {
      title: "Entity's Address",
      /**
       * Returns the export value for the "Entity's Address" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const isTrust = isTrustInvestment(investment);

        if (isTrust) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          address,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        return getAddress(address);
      },
    },
    {
      title: "Trust's Full Legal Name",
      /**
       * Returns the export value for the "Trust's Full Legal Name" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const isTrust = isTrustInvestment(investment);

        if (!isTrust) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        return profileEntity.name;
      },
    },
    {
      title: "Trust's Address",
      /**
       * Returns the export value for the "Trust's Address" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const isTrust = isTrustInvestment(investment);

        if (!isTrust) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          address,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        return getAddress(address);
      },
    },
    {
      title: "EIN",
      /**
       * Returns the export value for the "EIN" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { hasEinNumber } = profileEntity;

        if (!hasEinNumber) {
          return EMPTY_VALUE;
        }

        return profileEntity.einNumber;
      },
    },
    {
      title: "Entity Type",
      /**
       * Returns the export value for the "Entity Type" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const isTrust = isTrustInvestment(investment);

        if (isTrust) {
          return EMPTY_VALUE;
        }

        const { entityType, isDisregardedForTaxPurposes } = profileEntity;

        if (isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        return ENTITY_TYPE_TITLES_MAP[entityType];
      },
    },
    {
      title: "Tax Filing Status",
      /**
       * Returns the export value for the "Tax Filing Status" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          entityType,
          taxFilingStatus,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        const isLLC = entityType === ENTITY_TYPE_LLC;

        if (!isLLC) {
          return EMPTY_VALUE;
        }

        if (isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const title = taxFilingStatus
          ? ENTITY_TAX_FILING_STATUS_TITLES_MAP[taxFilingStatus]
          : "";

        return title;
      },
    },
    {
      title: "Retirement Plan (IRA/SEP/Keogh/etc.)",
      /**
       * Returns the export value for the "Retirement Plan (IRA/SEP/Keogh/etc.)" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          entityType,
          isRetirementPlan,
        } = profileEntity;

        const isExemptOrganization = entityType === ENTITY_TYPE_EXEMPT_ORGANIZATION;

        if (!isExemptOrganization) {
          return EMPTY_VALUE;
        }

        const hasRetirementPlanValue = isRetirementPlan !== undefined;

        if (!hasRetirementPlanValue) {
          return "";
        }

        const label = isRetirementPlan
          ? LABEL_YES
          : LABEL_NO;

        return label;
      },
    },
    {
      title: "Status (e.g. Disregarded or not)",
      /**
       * Returns the export value for the "Status (e.g. Disregarded or not)" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { isDisregardedForTaxPurposes } = profileEntity;

        const hasDisregardedForTaxPurposesValue = isDisregardedForTaxPurposes !== undefined;

        if (!hasDisregardedForTaxPurposesValue) {
          return "";
        }

        const label = isDisregardedForTaxPurposes
          ? LABEL_DISREGARDED
          : LABEL_NOT_DISREGARDED;

        return label;
      },
    },
    {
      title: "Beneficial Owner Name",
      /**
       * Returns the export value for the "Beneficial Owner Name" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { isDisregardedForTaxPurposes } = profileEntity;

        const name = isDisregardedForTaxPurposes
          ? profileEntity.beneficialOwnerName
          : EMPTY_VALUE;

        return name;
      },
    },
    {
      title: "Beneficial Owner Entity Type",
      /**
       * Returns the export value for the "Beneficial Owner Entity Type" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { isDisregardedForTaxPurposes } = profileEntity;

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const { beneficialOwnerEntityType } = profileEntity;

        const title = beneficialOwnerEntityType
          ? ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP[beneficialOwnerEntityType]
          : "";

        return title;
      },
    },
    {
      title: "Beneficial Owner Tax Filing Status",
      /**
       * Returns the export value for the "Beneficial Owner Tax Filing Status" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          beneficialOwnerEntityType,
          isDisregardedForTaxPurposes,
          beneficialOwnerTaxFilingStatus,
        } = profileEntity;

        const isLLC = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC;

        if (!isLLC) {
          return EMPTY_VALUE;
        }

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const title = beneficialOwnerTaxFilingStatus
          ? BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP[beneficialOwnerTaxFilingStatus]
          : "";

        return title;
      },
    },
    {
      title: "Beneficial Owner EIN",
      /**
       * Returns the export value for the "Beneficial Owner EIN" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          beneficialOwnerEntityType,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const isIRA = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;
        const isIndividual = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL;

        if (isIRA) {
          return EMPTY_VALUE;
        }

        if (isIndividual) {
          return EMPTY_VALUE;
        }

        return profileEntity.beneficialOwnerEinNumber;
      },
    },
    {
      title: "Beneficial Owner Custodian EIN",
      /**
       * Returns the export value for the "Beneficial Owner Custodian EIN" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          beneficialOwnerEntityType,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        const isIRA = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;

        if (!isIRA) {
          return EMPTY_VALUE;
        }

        return profileEntity.beneficialOwnerCustodianEinNumber;
      },
    },
    {
      title: "Beneficial Owner SSN/ITIN",
      /**
       * Returns the export value for the "Beneficial Owner SSN/ITIN" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const {
          hasSsn,
          isDisregardedForTaxPurposes,
        } = profileEntity;

        if (!isDisregardedForTaxPurposes) {
          return EMPTY_VALUE;
        }

        if (!hasSsn) {
          return EMPTY_VALUE;
        }

        return profileEntity.beneficialOwnerSsnNumber;
      },
    },
    {
      title: "Beneficial Owner Address",
      /**
       * Returns the export value for the "Beneficial Owner Address" column.
       */
      value: (investment) => {
        const isEntity = isEntityInvestment(investment);

        if (!isEntity) {
          return EMPTY_VALUE;
        }

        const { profileEntity } = investment;

        if (!profileEntity) {
          return undefined;
        }

        const { isDisregardedForTaxPurposes } = profileEntity;

        const address = isDisregardedForTaxPurposes
          ? getAddress(profileEntity.beneficialOwnerAddress)
          : EMPTY_VALUE;

        return address;
      },
    },
    {
      title: "SDIRA Type",
      /**
       * Returns the export value for the "SDIRA Type" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        const { sdiraType } = profileSdira;

        return SDIRA_TYPE_LABELS_MAP[sdiraType] || "";
      },
    },
    {
      title: "Entity’s Full Legal Name",
      /**
       * Returns the export value for the "Entity’s Full Legal Name" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        const {
          sdiraType,
          entityName,
        } = profileSdira;

        const isCheckbook = sdiraType === CHECKBOOK_CONTROLLED_SDIRA;

        if (!isCheckbook) {
          return EMPTY_VALUE;
        }

        return entityName;
      },
    },
    {
      title: "Entity has EIN",
      /**
       * Returns the export value for the "Entity has EIN" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        const {
          sdiraType,
          hasEinNumber,
        } = profileSdira;

        const isCheckbook = sdiraType === CHECKBOOK_CONTROLLED_SDIRA;

        if (!isCheckbook) {
          return EMPTY_VALUE;
        }

        const label = hasEinNumber
          ? LABEL_YES
          : LABEL_NO;

        return label;
      },
    },
    {
      title: "Entity EIN",
      /**
       * Returns the export value for the "Entity EIN" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        const {
          sdiraType,
          einNumber,
          hasEinNumber,
        } = profileSdira;

        const isCheckbook = sdiraType === CHECKBOOK_CONTROLLED_SDIRA;

        if (!isCheckbook) {
          return EMPTY_VALUE;
        }

        if (!hasEinNumber) {
          return EMPTY_VALUE;
        }

        return einNumber;
      },
    },
    {
      title: "Full Legal SDIRA Account Name",
      /**
       * Returns the export value for the "Full Legal SDIRA Account Name" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        return profileSdira.accountName;
      },
    },
    {
      title: "Custodian Name",
      /**
       * Returns the export value for the "Custodian Name" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        return profileSdira.custodianName;
      },
    },
    {
      title: "Custodian EIN",
      /**
       * Returns the export value for the "Custodian EIN" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        return profileSdira.custodianEinNumber;
      },
    },
    {
      title: "Custodian Address",
      /**
       * Returns the export value for the "Custodian Address" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (!isSdira) {
          return EMPTY_VALUE;
        }

        const { profileSdira } = investment;

        if (!profileSdira) {
          return undefined;
        }

        return getAddress(profileSdira.custodianAddress);
      },
    },
    {
      title: "State of Residence",
      /**
       * Returns the export value for the "State of Residence" column.
       */
      value: (investment) => {
        let region: string | undefined;
        let profile: ResidencyProfile | null = null;
        let isEntity = false;

        const {
          investor,
          profileJoint,
          profileSdira,
          profileEntity,
          profileIndividual,
        } = investment;

        const isIndividual =
          isIndividualInvestment(investment) &&
          !!profileIndividual;

        if (isIndividual) {
          profile = profileIndividual;

          const { address } = investor;
          region = address?.addressRegion;
        }

        const isJoint =
          isJointInvestment(investment) &&
          !!profileJoint;

        if (isJoint) {
          profile = profileJoint;

          region = (profileJoint.jointAddress || {}).addressRegion;
        }

        const isEntityProfile =
          isEntityInvestment(investment) &&
          !!profileEntity;

        if (isEntityProfile) {
          profile = profileEntity;
          isEntity = true;

          const { address, beneficialOwnerAddress } = profileEntity;
          region = address?.addressRegion || beneficialOwnerAddress?.addressRegion;
        }

        const isSdira =
          isSdiraInvestment(investment) &&
          !!profileSdira;

        if (isSdira) {
          const { address } = investor;

          return address?.addressRegion || "";
        }

        if (profile) {
          return getUsResidenceState(isEntity, profile, region);
        }

        return "";
      },
    },
    {
      title: "Non-US Person",
      /**
       * Returns the export value for the "Non-US Person" column.
       */
      value: (investment) => {
        const isSdira = isSdiraInvestment(investment);

        if (isSdira) {
          return LABEL_NO;
        }

        const profile = getProfile(investment);

        if (!profile) {
          return "";
        }

        const { isUsPerson, isUsCitizen } = profile;

        if (isUsCitizen) {
          return LABEL_NO;
        }

        const hasUsPersonValue = isUsPerson !== undefined;

        if (!hasUsPersonValue) {
          return "";
        }

        if (isUsPerson) {
          return LABEL_NO;
        }

        return LABEL_YES;
      },
    },
    {
      title: "Distribution Account Mask",
      /**
       * Returns the export value for the "Distribution Account Mask" column.
       */
      value: ({ receiverSourceName }) => receiverSourceName,
    },
    {
      title: "Subscription Status",
      /**
       * Returns the export value for the "Subscription Status" column.
       */
      value: ({ isSigned }) => {
        const status = isSigned
          ? LABEL_SIGNED
          : LABEL_PENDING;

        return status;
      },
    },
    ...classColumns,
    {
      title: "Total Committed",
      /**
       * Returns the export value for the "Total Committed" column.
       */
      value: (investment) => {
        const amount = computeInvestmentAmount(investment);

        return getCurrencyValue(amount);
      },
    },
    {
      title: "Funds Received",
      /**
       * Returns the export value for the "Funds Received" column.
       */
      value: ({ receivedAmount }) => getCurrencyValue(receivedAmount),
    },
    {
      title: "Created At",
      /**
       * Returns the export value for the "Created At" column.
       */
      value: ({ createdAt }) => getUsDateFromIsoDate(createdAt),
    },
  ];
};

export default getInvestmentExportColumns;
